import React from 'react';
import Select, { components } from 'react-select';
import styled, { useTheme } from 'styled-components';
import { ParagraphLargeText } from '../../Texts';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';
import ChevronRightIcon from '../../../assets/icons/chevron-right.svg?react';
import { hapticsImpactLight } from '../../../common/haptic';
import { useDispatch } from 'react-redux';
import { openModal } from '../../../redux/slices/modals';
import PlusIcon from '../../../assets/icons/plus.svg?react';
import { openSelect } from '../../../redux/slices/select';
import { ErrorMessage } from '../Text';

const Label = styled.label`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.text.secondary};
`;

const SelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: fit-content;
  opacity: ${({ $disabled }) => ($disabled ? 0.7 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  min-width: 105px;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  border-radius: 16px;
  width: 100%;
  box-sizing: border-box;
  padding: 0px;
`;

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
`;

const Name = styled(ParagraphLargeText)`
  color: ${({ theme }) => theme.text.regular};
  font-size: 16px;
  font-weight: 400;
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 14px;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const SingleValueContainer = styled.div`
  padding-right: 10px;
`;

const DisabledValue = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: hsl(0, 0%, 20%);
`;

const StyledSelectContainer = styled.div`
  flex: 1;
  min-width: 0;
  .react-select__control {
    background-color: ${({ theme }) => theme.background.light};
    border: none;
    box-shadow: none;
    padding: 8px;
    padding-left: 16px;
    min-height: 48px;
  }
`;

const Option = ({ data, ...props }) => (
  <components.Option {...props}>
    <OptionContainer>
      <Name>{data.label}</Name>
    </OptionContainer>
  </components.Option>
);

const AddNewOption = ({ onClick, label = 'Add new address' }) => {
  const theme = useTheme();
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px',
        cursor: 'pointer',
        borderTop: `1px solid ${theme.border.secondary}`,
        backgroundColor: theme.background.primary,
        color: '#007bff',
        fontWeight: '500',
      }}
    >
      <PlusIcon width={16} height={16} />
      <Name style={{ color: '#007bff', fontWeight: '500' }}>{label}</Name>
    </div>
  );
};

const SingleValue = ({ data, ...props }) => (
  <components.SingleValue {...props}>
    <SingleValueContainer>
      <Name>{data.label}</Name>
    </SingleValueContainer>
  </components.SingleValue>
);

const SelectedOptionContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${({ theme }) => theme.background.light};
  border-radius: 16px;
  cursor: pointer;
  padding-left: 15px;
  padding-right: 15px;
  height: 48px;
`;

const ValueText = styled(ParagraphLargeText)`
  color: ${({ theme }) => theme.text.regular};
  font-size: 16px;
  font-weight: 400;
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 14px;
  }
`;

const Selector = ({
  label,
  value,
  disabled,
  onChange,
  options,
  placeholder,
  noOptionsMessage,
  noOptionsAction,
  showAddNew = false,
  addNewLabel = 'Add new address',
  onAddNew,
  error,
}) => {
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const handleChange = (option) => {
    onChange?.(option ? option.value : null);
  };

  // Create a custom MenuList component that includes the add new option
  const MenuList = (props) => (
    <components.MenuList {...props}>
      {props.children}
      {showAddNew && onAddNew && (
        <AddNewOption onClick={onAddNew} label={addNewLabel} />
      )}
    </components.MenuList>
  );

  const theme = useTheme();

  const selectedOption = value ? options?.find((o) => o.value === value) : null;

  const dispatch = useDispatch();

  // For mobile, add the "add new" option to the options array if needed
  const mobileOptions =
    showAddNew && onAddNew
      ? [
          ...options,
          {
            value: 'add-new',
            label: addNewLabel,
            isAddNew: true,
          },
        ]
      : options;

  if (isMobile) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: '100%',
        }}
      >
        <Label>{label}</Label>
        <SelectedOptionContainer
          onClick={() => {
            hapticsImpactLight();
            if (options.length === 0) {
              noOptionsAction?.();
              return;
            } else {
              dispatch(
                openSelect({
                  options: mobileOptions,
                  placeHolder: placeholder,
                  selectedOption: value,
                  onChange: (option) => {
                    if (option === 'add-new') {
                      onAddNew?.();
                      return;
                    }
                    onChange?.(option);
                  },
                }),
              );
            }
          }}
        >
          {options.length === 0 ? (
            <>
              <ValueText>{noOptionsMessage}</ValueText>
              <PlusIcon width={12} height={12} />
            </>
          ) : (
            <>
              {!value ? (
                <>
                  <ValueText>{placeholder}</ValueText>
                  <ChevronRightIcon
                    width={12}
                    height={12}
                    style={{ color: theme.text.title }}
                  />
                </>
              ) : (
                <>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <ValueText>{selectedOption?.label}</ValueText>
                  </div>
                  <ChevronRightIcon
                    width={12}
                    height={12}
                    style={{ color: theme.text.title }}
                  />
                </>
              )}
            </>
          )}
        </SelectedOptionContainer>
        {error && (
          <div style={{ width: '100%' }}>
            <ErrorMessage style={{ textAlign: 'flex-start' }}>
              {error}
            </ErrorMessage>
          </div>
        )}
      </div>
    );
  }

  console.log('selectedOption', selectedOption);

  return (
    <SelectorContainer $disabled={disabled}>
      <Row>
        <InputContainer>
          <StyledSelectContainer>
            <Label>{label}</Label>
            <Select
              className='react-select'
              classNamePrefix='react-select'
              isDisabled={disabled}
              isSearchable
              noOptionsMessage={
                noOptionsAction &&
                noOptionsMessage &&
                (() => (
                  <OptionContainer onClick={noOptionsAction}>
                    <Name>{noOptionsMessage}</Name>
                  </OptionContainer>
                ))
              }
              styles={{
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: theme.background.primary,
                  padding: '8px',
                  margin: 0,
                  color: state.isSelected
                    ? `${theme.primary.accent}`
                    : `${theme.text.regular}`,
                }),
                menuList: (provided) => ({
                  ...provided,
                  margin: 0,
                  padding: 0,
                  borderBottom: `1px solid ${theme.border.secondary}`,
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  borderLeft: `1px solid ${theme.border.secondary}`,
                  borderRight: `1px solid ${theme.border.secondary}`,
                  maxHeight: '200px',
                }),
                control: (provided) => ({
                  ...provided,
                  backgroundColor: theme.background.primary,
                  border: `1px solid ${theme.border.secondary}`,
                  borderRadius: 16,
                  height: '46px',
                }),
                controlHovered: (provided) => ({
                  ...provided,
                  border: '1px solid red',
                }),
                menu: (provided) => ({
                  ...provided,
                  paddingTop: 10,
                  height: 'fit-content',
                  paddingBottom: 10,
                  zIndex: 1000,
                  margin: 0,
                  padding: 0,
                }),
              }}
              options={options}
              value={selectedOption}
              onChange={(opt) => handleChange(opt)}
              placeholder={placeholder}
              components={{
                Option,
                SingleValue,
                MenuList: showAddNew ? MenuList : components.MenuList,
              }}
            />
            {error && (
              <div style={{ width: '100%' }}>
                <ErrorMessage style={{ textAlign: 'flex-start' }}>
                  {error}
                </ErrorMessage>
              </div>
            )}
          </StyledSelectContainer>
        </InputContainer>
      </Row>
    </SelectorContainer>
  );
};

export default Selector;
